import { FC } from 'react'

import { Box, Card, Divider, Skeleton } from '@mui/material'
import { alpha } from '@mui/material/styles'

import { SectionCard } from '../SectionCard/SectionCard'
import { TableSkeleton } from '../TableSkeleton/TableSkeleton'

/**
 * Layout-matched skeleton for `UAInsightsDashboard`. Mirrors the real showcase
 * overview: the sticky header bar (network / month / genre selectors + search),
 * the three summary cards, the Monthly Insight card, and the three stacked
 * sections (rising story patterns, creative motivations chart, story patterns
 * table). Renders in place of a full-page spinner so the dashboard keeps its
 * shape on first load and does not visibly reflow when data arrives.
 */
export const DashboardSkeleton: FC = () => (
  <Box>
    {/* Sticky header bar mock (network / month / genre selectors + search). */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: -2.3, pt: 1.5, pb: 1.5, mb: 1, borderBottom: 1, borderColor: 'divider' }}>
      <Skeleton variant="rounded" width={120} height={40} />
      <Skeleton variant="rounded" width={120} height={40} />
      <Skeleton variant="rounded" width={160} height={40} />
      <Skeleton variant="rounded" sx={{ flex: 1 }} height={40} />
    </Box>

    {/* Three summary cards. */}
    <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
      {[0, 1, 2].map((i) => (
        <Card key={i} variant="outlined" sx={{ flex: 1, minWidth: 0, px: 2.5, py: 1.5 }}>
          <Skeleton variant="text" width={120} height={14} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width={140} height={48} />
        </Card>
      ))}
    </Box>

    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
      {/* Monthly Insight card (circular icon badge + overline/title, a divider, then body). */}
      <Card elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: (theme) => alpha(theme.palette.primary.main, 0.16) }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Skeleton variant="circular" width={38} height={38} sx={{ flexShrink: 0 }} />
          <Box>
            <Skeleton variant="text" width={96} height={14} sx={{ mb: 0.5 }} />
            <Skeleton variant="text" width={220} height={20} />
          </Box>
        </Box>
        <Divider sx={{ my: 1.5 }} />
        <Skeleton variant="text" width="100%" height={16} />
        <Skeleton variant="text" width="92%" height={16} />
        <Skeleton variant="text" width="60%" height={16} />
      </Card>

      {/* Top rising story patterns — a row of compact cards. */}
      <SectionCard title={<Skeleton variant="text" width={180} height={24} />}>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" height={96} sx={{ flex: 1, borderRadius: 1 }} />
          ))}
        </Box>
      </SectionCard>

      {/* Creative motivations chart. */}
      <SectionCard title={<Skeleton variant="text" width={200} height={24} />}>
        <Skeleton variant="rounded" height={280} sx={{ borderRadius: 1 }} />
      </SectionCard>

      {/* Creative story patterns table. */}
      <SectionCard title={<Skeleton variant="text" width={220} height={24} />} disableBodyPadding>
        <TableSkeleton rows={8} columns={5} bare />
      </SectionCard>
    </Box>
  </Box>
)
